import classNames from 'classnames'
import { format } from 'date-fns'
import HTMLReactParser from 'html-react-parser'
import { FC, useState } from 'react'
import styles from 'styles/components/card.module.scss'
import text from 'styles/utility/text.module.scss'
import button from 'styles/components/button.module.scss'
import { FlexRow } from 'components/Flex'
import { BlueButton, GreyButton, OrangeButton } from 'components/Button'
import { Spacer } from 'components/Utility'
import { TextButton } from 'components/Button'
import { returnCodeToBr } from 'lib/helper'
import { SubDescription } from 'components/Typography'
import { Textarea } from 'components/Form'
import { useString } from 'hooks/form'
import { FirebaseDocRefType, FirebaseTimestamp } from 'firebase/index'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { getStoreRef } from 'reducks/auth/selectors'
import { updateStoreCustomerNotes } from 'repos/storeCustomer'
import { closeModal } from 'reducks/modal/operations'
import { useFetchStoreCustomer } from 'hooks/customer'

type Props = {
  userId: string
  isShownByModal?: boolean
}

const CustomerCard: FC<Props> = (props) => {
  const { userId, isShownByModal } = props
  const dispatch = useDispatch()
  const storeRef: FirebaseDocRefType = useSelector((state: State) => getStoreRef(state))
  const { storeCustomer, setStoreCustomer } = useFetchStoreCustomer(storeRef, userId)
  const [noteIndex, setNoteIndex] = useState<number>(-1)
  const { handleState: handleNote, state: note, setState: setNote } = useString<string>('')

  const isDisabled = note.length === 0 || note.length > 300

  // notes が10件以上の場合は追加できないようにする
  const isReachedMaximumNotes = (storeCustomer?.notes.length ?? 0) >= 10

  const editNote = ({ note, index }: { note: string; index: number }) => {
    setNoteIndex(index)
    setNote(note)
  }

  const addNote = () => {
    if (isReachedMaximumNotes || !storeCustomer) return
    setNoteIndex(storeCustomer.notes.length + 1)
  }

  const onCloseNote = () => {
    setNoteIndex(-1)
    setNote('')
  }

  const handleSubmit = async ({ note, index }: { note: string; index: number }) => {
    if (!storeCustomer) return
    const now = FirebaseTimestamp.now()
    if (storeCustomer.notes[index]) {
      storeCustomer.notes[index].note = note
      storeCustomer.notes[index].updated_at = now
      await updateStoreCustomerNotes({
        storeRef,
        userId: storeCustomer.user_id,
        notes: storeCustomer.notes,
      })
      setStoreCustomer({ ...storeCustomer })
      dispatch(closeModal())
    } else {
      storeCustomer.notes = [
        ...storeCustomer.notes,
        {
          note,
          created_at: now,
          updated_at: now,
        },
      ]
      await updateStoreCustomerNotes({
        storeRef,
        userId: storeCustomer.user_id,
        notes: storeCustomer.notes,
      })
      setStoreCustomer({ ...storeCustomer })
      dispatch(closeModal())
    }
  }

  return (
    <div>
      {storeCustomer !== undefined && (
        <div className={styles.customer__body}>
          <div className={styles.customer__items}>
            <div>
              <FlexRow justifyContent={'start'} alignItems={'center'}>
                <img
                  className={styles.customer__image}
                  src={storeCustomer.avatar_url || '/icon/grey/ellipsis.svg'}
                  alt={storeCustomer.name}
                />
                <Spacer size={12} />
                <h2 className={classNames(text.color_black, text.font_bold)}>{storeCustomer.name}さま</h2>
              </FlexRow>
            </div>
            <Spacer size={10} />
            <div>
              <h2 className={classNames(text.color_orange_main, text.font_mediumLarge, text.fonr_bold)}>基本情報</h2>
              <Spacer size={8} />
              <FlexRow justifyContent={'start'}>
                <span className={classNames(text.font_small, text.color_black)}>購入回数：</span>
                <Spacer size={2} />
                <span className={classNames(text.font_small, text.color_black)}>{storeCustomer.purchase_count}回</span>
              </FlexRow>
              {storeCustomer.last_purchased_at !== null && (
                <>
                  <Spacer size={8} />
                  <FlexRow justifyContent={'start'}>
                    <span className={classNames(text.font_small, text.color_black)}>最終購入日：</span>
                    <Spacer size={2} />
                    <span className={classNames(text.font_small, text.color_black)}>
                      {format(storeCustomer.last_purchased_at.toDate(), 'yyyy年M月d日')}
                    </span>
                  </FlexRow>
                </>
              )}
              <Spacer size={8} />
              <FlexRow justifyContent={'start'}>
                <span className={classNames(text.font_small, text.color_black)}>電話番号：</span>
                <Spacer size={2} />
                <span className={classNames(text.font_small, text.color_black)}>{storeCustomer.tel}</span>
              </FlexRow>
              <Spacer size={8} />
              <FlexRow justifyContent={'start'}>
                <span className={classNames(text.font_small, text.color_black)}>ユーザーID：</span>
                <Spacer size={2} />
                <span className={classNames(text.font_small, text.color_black)}>{storeCustomer.user_id}</span>
              </FlexRow>
            </div>
            <Spacer size={14} />
            <div>
              <h2 className={classNames(text.color_orange_main, text.font_mediumLarge, text.fonr_bold)}>
                よく購入している商品
              </h2>
              <Spacer size={8} />
              <SubDescription
                description={
                  '※購入された商品点数が多い上位5種類まで表示しています。\n同一点数がある場合は、アプリの並び順が早い順が表示されます。'
                }
                size={'small'}
              />
              <Spacer size={8} />
              <div>
                <div className={styles.customer__header}>
                  <span className={styles.customer__header__cell}>商品名</span>
                  <span className={styles.customer__header__cell}>個数(個)</span>
                </div>
                {storeCustomer.frequently_purchased_breads.map((frequentlyPurchasedBread) => (
                  <li key={frequentlyPurchasedBread.bread_ref.id} className={styles.customer__row}>
                    <div className={styles.customer__row__label}>
                      <span
                        className={classNames(
                          styles.customer__row__cell,
                          text.font_medium,
                          text.font_bold,
                          text.color_blue_main
                        )}
                      >
                        {frequentlyPurchasedBread.bread_name}
                      </span>
                      <span
                        className={classNames(
                          styles.customer__row__cell,
                          text.font_medium,
                          text.font_bold,
                          text.color_black
                        )}
                      >
                        {frequentlyPurchasedBread.purchase_count}
                      </span>
                    </div>
                  </li>
                ))}
              </div>
              <Spacer size={14} />
              <div className={styles.customer__notes}>
                <Spacer size={10} />
                <h2 className={classNames(text.color_orange_main, text.font_mediumLarge, text.fonr_bold)}>ノート</h2>
                {storeCustomer.notes.length === 0 ? (
                  <div>
                    <Spacer size={10} />
                    <FlexRow justifyContent={'center'}>
                      <p className={classNames(text.font_bold, text.font_small)}>
                        お客さまに関する情報を記録しておくことができます。
                      </p>
                    </FlexRow>
                  </div>
                ) : (
                  <div>
                    {storeCustomer.notes.map((note, i) => (
                      <li key={i} className={styles.customer__noteList}>
                        <Spacer size={8} />
                        <div className={styles.customer__note}>
                          <div className={styles.customer__label}>
                            <p className={classNames(text.font_small, text.font_bold)}>
                              {format(note.created_at.toDate(), 'yyyy.MM.dd')}
                            </p>
                            <BlueButton
                              label={'編集'}
                              onClick={() => editNote({ note: note.note, index: i })}
                              size={'xxxsmall'}
                              type={'outlined'}
                            />
                          </div>
                          <Spacer size={8} />
                          <p className={text.font_xxsmall}>{HTMLReactParser(returnCodeToBr(note.note))}</p>
                          <Spacer size={8} />
                        </div>
                      </li>
                    ))}
                  </div>
                )}
              </div>
              <Spacer size={10} />
              {noteIndex === -1 ? (
                <FlexRow justifyContent={'center'}>
                  <div className={classNames(button.button_blue_outlined, button.button_xsmall)}>
                    <TextButton
                      disabled={isReachedMaximumNotes}
                      label={'ノートを追加'}
                      iconPath={'/icon/blue/plus.svg'}
                      onClick={addNote}
                      size={'small'}
                    />
                  </div>
                </FlexRow>
              ) : (
                <div>
                  <Textarea
                    disabled={false}
                    label={''}
                    maxLength={300}
                    onChange={handleNote}
                    required={false}
                    rows={5}
                    value={note}
                  />
                  <Spacer size={16} />
                  <FlexRow justifyContent={'center'}>
                    <GreyButton label={'キャンセル'} onClick={onCloseNote} size={'xsmall'} type={'outlined'} />
                    <Spacer size={10} />
                    <OrangeButton
                      disabled={isDisabled}
                      label={'保存する'}
                      onClick={() => {
                        handleSubmit({ note, index: noteIndex })
                        onCloseNote()
                      }}
                      size={'xsmall'}
                      type={'main'}
                    />
                  </FlexRow>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
export default CustomerCard
